import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Loader, Pencil, Search } from "lucide-react";
import { useCurrencies } from "../hooks/useCurrencies";
import CurrencyEditForm from "./CurrencyEditForm";

export function CurrencyTable() {
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, isError, error } = useCurrencies(
    debouncedSearch || undefined
  );
  // Unfiltered list so the edit form's "other currencies" picker (e.g. rate
  // providers) isn't limited to whatever the search box currently matches.
  const { data: allCurrenciesData } = useCurrencies();

  const handleEdit = (currency) => {
    setSelectedCurrency(currency);
    setIsEditOpen(true);
  };

  const columns = [
    {
      accessorKey: "currencyCode",
      header: "Currency",
      cell: (info) => (
        <span className="font-semibold text-gray-800 uppercase">{info.getValue()}</span>
      ),
    },
    {
      id: "isHub",
      header: "Hub",
      accessorFn: (row) => row.isHub,
      cell: (info) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
            info.getValue() ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-500"
          }`}
        >
          {info.getValue() ? "Hub" : "—"}
        </span>
      ),
    },
    {
      id: "currencyClass",
      header: "Class",
      accessorKey: "currencyClass",
      cell: (info) => (
        <span className="text-sm text-gray-600 capitalize">{info.getValue() || "local"}</span>
      ),
    },
    {
      id: "rateProviders",
      header: "Rate Providers (order)",
      accessorFn: (row) => row.rateProviders,
      cell: (info) => {
        const providers = info.getValue() || [];
        return (
          <span className="text-sm text-gray-600">
            {providers.length ? providers.join(" → ") : "Not configured"}
          </span>
        );
      },
    },
    {
      id: "spreads",
      header: "Buy / Sell Spread",
      cell: ({ row }) => (
        <span className="text-sm text-gray-600">
          {row.original.buySpread} / {row.original.sellSpread} ({row.original.spreadType})
        </span>
      ),
    },
    {
      id: "fee",
      header: "Conversion Fee %",
      cell: ({ row }) => (
        <span className="text-sm text-gray-600">
          {row.original.conversionFeePercentage ?? 0}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <button
          onClick={() => handleEdit(row.original)}
          className="flex gap-1 items-center text-sm font-medium text-emerald-600 hover:text-emerald-800"
        >
          <Pencil size={14} /> Edit
        </button>
      ),
    },
  ];

  const records = useMemo(() => data?.data || [], [data]);

  const table = useReactTable({
    data: records,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[400px]">
        <div className="flex flex-1 justify-center items-center">
          <p className="flex gap-2 items-center font-medium text-gray-600">
            Loading currencies <Loader className="w-5 h-5 animate-spin" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="relative mb-4 max-w-xs">
        <Search className="absolute top-1/2 left-3 w-4 h-4 text-gray-400 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by currency, country, or symbol"
          className="py-2 pr-3 pl-9 w-full text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <div className="overflow-hidden rounded border border-gray-200">
        <div className="overflow-x-auto min-h-[300px]">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gray-50/50">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {records.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="transition-colors hover:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                    {search ? `No currencies match "${search}"` : "No currencies found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CurrencyEditForm
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        currency={selectedCurrency}
        allCurrencies={allCurrenciesData?.data || []}
      />
    </div>
  );
}

export default CurrencyTable;
